import React, { FC, forwardRef, HTMLAttributes, useMemo } from 'react';

import cn from 'classnames';

import { Pill, Text } from '@components/common';
import { NEW_STUDY_PALETTE } from '@components/StudiesApp/components/StudyNew/constants';
import { NewStudyKind } from '@components/StudiesApp/components/StudyNew/types';
import { Tooltip } from '@components/Tooltip';

import * as consts from './constants';

interface Props extends Omit<HTMLAttributes<HTMLButtonElement>, 'children'> {
  kind: NewStudyKind;
  showBadge?: boolean | string;
  disabled?: boolean;
  tooltip?: string;
}

type Mapping = Record<
  NewStudyKind,
  {
    title: string;
    subtitle: string;
  }
>;

const NewStudyIcon = ({ kind }: { kind: NewStudyKind }) => (
  <div
    className='flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md'
    style={{ backgroundColor: NEW_STUDY_PALETTE[kind] }}
  >
    <svg width='22' height='22'>
      <use href={`#newStudy-${kind}`} />
    </svg>
  </div>
);

export const mapping: Mapping = {
  // Moderated
  video_call: {
    title: consts.VIDEO_CALL_TITLE,
    subtitle: consts.VIDEO_CALL_SUBTITLE
  },
  focus_groups: {
    title: consts.FOCUS_GROUPS_TITLE,
    subtitle: consts.FOCUS_GROUPS_SUBTITLE
  },
  round_robin: {
    title: consts.ROUND_ROBIN_TITLE,
    subtitle: consts.ROUND_ROBIN_SUBTITLE
  },
  collective: {
    title: consts.COLLECTIVE_TITLE,
    subtitle: consts.COLLECTIVE_SUBTITLE
  },
  continuous: {
    title: consts.CONTINUOUS_TITLE,
    subtitle: consts.CONTINUOUS_SUBTITLE
  },
  // Unmoderated
  unmoderated_test: {
    title: consts.UNMODERATED_TITLE,
    subtitle: consts.UNMODERATED_SUBTITLE
  },
  prototype_test: {
    title: consts.PROTOTYPE_TEST_TITLE,
    subtitle: consts.PROTOTYPE_TEST_SUBTITLE
  },
  card_sort: {
    title: consts.CARD_SORT_TITLE,
    subtitle: consts.CARD_SORT_SUBTITLE
  },
  tree_test: {
    title: consts.TREE_TEST_TITLE,
    subtitle: consts.TREE_TEST_SUBTITLE
  },
  ai_moderation: {
    title: consts.AI_MODERATION_TITLE,
    subtitle: consts.AI_MODERATION_SUBTITLE
  },
  five_second_test: {
    title: consts.FIVE_SECOND_TEST_TITLE,
    subtitle: consts.FIVE_SECOND_TEST_SUBTITLE
  },
  first_click_test: {
    title: consts.FIRST_CLICK_TEST_TITLE,
    subtitle: consts.FIRST_CLICK_TEST_SUBTITLE
  },
  preference_test: {
    title: consts.PREFERENCE_TEST_TITLE,
    subtitle: consts.PREFERENCE_TEST_SUBTITLE
  },
  // Other
  survey: {
    title: consts.SURVEY_TITLE,
    subtitle: consts.SURVEY_SUBTITLE
  },

  online_task: {
    title: consts.ONLINE_TASK_TITLE,
    subtitle: consts.ONLINE_TASK_SUBTITLE
  },
  panel: {
    title: consts.PANEL_TITLE,
    subtitle: consts.PANEL_SUBTITLE
  }
};

export const StudyButton = forwardRef<HTMLButtonElement, Props>(
  ({ kind, showBadge, className, disabled = false, onClick, tooltip, ...rest }: Props, ref) => {
    return (
      <Tooltip className='w-full' content={tooltip} isDisabled={!tooltip}>
        <button
          ref={ref}
          data-testid={`study-button-${kind}`}
          aria-disabled={disabled}
          className={cn(
            'flex h-20 w-full items-center rounded border border-gray-200 bg-white px-3',
            {
              'hover:border-indigo-600': !disabled,
              'cursor-not-allowed bg-gray-50 text-gray-500': disabled
            },
            className
          )}
          onClick={disabled ? undefined : onClick}
          {...rest}
        >
          <div className='flex h-10 w-10 items-center justify-center rounded-md bg-green-500'>
            <NewStudyIcon kind={kind} />
          </div>
          <section className='flex flex-grow-0 flex-col items-start pl-4 text-left'>
            <Text className={cn('mb-1', showBadge && 'flex items-center')} bold>
              <span className='mr-2'>{mapping[kind].title}</span>
              {(showBadge === true || showBadge === 'beta') && (
                <>
                  <Pill color='blue'>Beta</Pill>
                </>
              )}
              {showBadge === 'free_trial' && (
                <>
                  <Pill color='blue'>Free Trial</Pill>
                </>
              )}
              {showBadge === 'coming_soon' && (
                <>
                  <Pill color='gray'>Coming Soon</Pill>
                </>
              )}
            </Text>
            <Text h='400' className='text-gray-500'>
              {mapping[kind].subtitle}
            </Text>
          </section>
        </button>
      </Tooltip>
    );
  }
);
